import { CONFIG } from "../../../config";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

const CRMCONFIG = CONFIG.modules["crm"];
const API_HOST_CRM = CRMCONFIG.host;

// Development logging utility
const isDevelopment = process.env.NODE_ENV === "development";
const devLog = (message, data) => {
  if (isDevelopment) {
    console.log(message, data);
  }
};

/**
 * Custom hook for fetching simple case category analytics data without delivery type separation
 * @param {Object} queries - Query parameters for the API call
 * @param {Object} options - Additional configuration options
 * @param {boolean} options.enabled - Whether the query should be enabled
 * @param {number} options.staleTime - Time in milliseconds before data is considered stale
 * @param {number} options.cacheTime - Time in milliseconds to keep data in cache
 * @param {number} options.retry - Number of retry attempts on failure
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch on window focus
 * @returns {Object} React Query result object
 */
export const useGetOnlyCaseCategoryAnalytics = (queries = {}, options = {}) => {
  // Validate inputs
  const validatedQueries = useMemo(() => {
    if (!queries || typeof queries !== "object") {
      devLog(
        "[useGetOnlyCaseCategoryAnalytics] Invalid queries parameter, using empty object",
      );
      return {};
    }
    return queries;
  }, [queries]);

  // Default options with user overrides
  const queryOptions = useMemo(
    () => ({
      enabled: true,
      refetchOnWindowFocus: false,
      retry: 2,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    }),
    [options],
  );

  // Memoized query key for better performance
  const queryKey = useMemo(() => {
    const sortedEntries = Object.entries(validatedQueries)
      .filter(([, value]) => value !== undefined && value !== null)
      .sort(([a], [b]) => a.localeCompare(b));

    return ["onlyCaseCategoryAnalytics", ...sortedEntries.flat()];
  }, [validatedQueries]);

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchOnlyCaseCategoryAnalytics = useCallback(async () => {
    try {
      // Filter out undefined/null values from params
      const cleanParams = Object.fromEntries(
        Object.entries(validatedQueries).filter(
          ([, value]) => value !== undefined && value !== null && value !== "",
        ),
      );

      devLog(
        "[useGetOnlyCaseCategoryAnalytics] Making API call with params:",
        cleanParams,
      );

      const response = await axios.get(
        `${API_HOST_CRM}/analytics/only-case-category-analytics`,
        {
          params: cleanParams,
          timeout: 30000, // 30 second timeout
        },
      );

      devLog("[useGetOnlyCaseCategoryAnalytics] Backend Data:", response.data);

      // Basic response validation
      if (!response.data) {
        throw new Error("No data received from server");
      }

      return response.data;
    } catch (error) {
      // Enhanced error handling with network detection
      const isNetworkError = !error.response && error.code === "ENOTFOUND";
      const isTimeoutError = error.code === "ECONNABORTED";

      let errorMessage = "Error fetching simple case category analytics";

      if (isNetworkError) {
        errorMessage += ": Network connection failed";
      } else if (isTimeoutError) {
        errorMessage += ": Request timeout";
      } else if (error.response) {
        errorMessage += `: Server responded with ${error.response.status}`;
      }

      console.error(errorMessage, {
        originalError: error,
        params: validatedQueries,
        timestamp: new Date().toISOString(),
      });

      throw new Error(errorMessage);
    }
  }, [validatedQueries]);

  return useQuery({
    queryKey,
    queryFn: fetchOnlyCaseCategoryAnalytics,
    enabled: queryOptions.enabled,
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus,
    retry: queryOptions.retry,
    staleTime: queryOptions.staleTime,
    cacheTime: queryOptions.cacheTime,
    onError: (error) => {
      console.error("Error in useGetOnlyCaseCategoryAnalytics:", {
        error: error.message,
        queries: validatedQueries,
        timestamp: new Date().toISOString(),
      });
    },
    // Performance optimization: only select data if it exists
    select: (data) => data || null,
  });
};

/**
 * Custom hook for fetching simple case category analytics data with a date range
 * @param {Object} fromDate - Start date for the query
 * @param {Object} toDate - End date for the query
 * @param {string} dataType - Type of data to fetch (e.g., 'all', 'follow up type shi')
 * @param {string} fetchKey - Additional key for query differentiation
 * @returns {Object} React Query result object
 */
export const useGetOnlyCaseCategoryAnalyticsRange = (
  queries = {},
  options = {},
) => {
  // Validate inputs
  const validatedQueries = useMemo(() => {
    if (!queries || typeof queries !== "object") {
      devLog(
        "[useGetOnlyCaseCategoryAnalyticsRange] Invalid queries parameter, using empty object",
      );
      return {};
    }
    return queries;
  }, [queries]);

  // Default options with user overrides
  const queryOptions = useMemo(
    () => ({
      enabled: true,
      refetchOnWindowFocus: false,
      retry: 2,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    }),
    [options],
  );

  // Memoized query key for better performance
  const queryKey = useMemo(() => {
    const sortedEntries = Object.entries(validatedQueries)
      .filter(([, value]) => value !== undefined && value !== null)
      .sort(([a], [b]) => a.localeCompare(b));

    return ["onlyCaseCategoryAnalyticsRange", ...sortedEntries.flat()];
  }, [validatedQueries]);

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchOnlyCaseCategoryAnalytics = useCallback(async () => {
    try {
      // Filter out undefined/null values from params
      const cleanParams = Object.fromEntries(
        Object.entries(validatedQueries).filter(
          ([, value]) => value !== undefined && value !== null && value !== "",
        ),
      );

      devLog(
        "[useGetOnlyCaseCategoryAnalyticsRange] Making API call with params:",
        cleanParams,
      );

      const response = await axios.get(
        `${API_HOST_CRM}/analytics/only-case-category-analytics`,
        {
          params: cleanParams,
          timeout: 30000, // 30 second timeout
        },
      );

      devLog(
        "[useGetOnlyCaseCategoryAnalyticsRange] Backend Data:",
        response.data,
      );

      // Basic response validation
      if (!response.data) {
        throw new Error("No data received from server");
      }

      return response.data;
    } catch (error) {
      // Enhanced error handling with network detection
      const isNetworkError = !error.response && error.code === "ENOTFOUND";
      const isTimeoutError = error.code === "ECONNABORTED";

      let errorMessage = "Error fetching simple case category analytics";

      if (isNetworkError) {
        errorMessage += ": Network connection failed";
      } else if (isTimeoutError) {
        errorMessage += ": Request timeout";
      } else if (error.response) {
        errorMessage += `: Server responded with ${error.response.status}`;
      }

      console.error(errorMessage, {
        originalError: error,
        params: validatedQueries,
        timestamp: new Date().toISOString(),
      });

      throw new Error(errorMessage);
    }
  }, [validatedQueries]);

  return useQuery({
    queryKey,
    queryFn: fetchOnlyCaseCategoryAnalytics,
    enabled: queryOptions.enabled,
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus,
    retry: queryOptions.retry,
    staleTime: queryOptions.staleTime,
    cacheTime: queryOptions.cacheTime,
    onError: (error) => {
      console.error("Error in useGetOnlyCaseCategoryAnalyticsRange:", {
        error: error.message,
        queries: validatedQueries,
        timestamp: new Date().toISOString(),
      });
    },
    // Performance optimization: only select data if it exists
    select: (data) => data || null,
  });
};
